import { i18n } from '@/plugins/i18n'
import { isValidDate, now, parseDateString } from '@/services/date-helper.service'
import { formatPrice } from '@/services/helper.service.ts'
import { isNotNullish } from '@/helpers/type-checks.ts'
import type { ValidationFn } from '@wgg.hyve/linexo-form-validator'

export type FieldRule = {
  type: string
  error_text: string
  arg: any
  field: string
}

export interface FieldRuleService {
  getRules(ruleList: Array<FieldRule>): ValidationFn[]
}

export const FieldRuleService: FieldRuleService = {
  getRules(ruleList: FieldRule[]): ValidationFn[] {
    return (ruleList || []).map((ruleConfig: FieldRule) => createRules(ruleConfig))
  }
}

const createRules = (config: FieldRule): ValidationFn => {
  const { t } = i18n.global
  const { field, error_text, arg, type } = config

  const ruleMap: { [key: string]: ValidationFn } = {
    required: (fieldValue: any) => {
      const errorText = field ? t(error_text, { field: t(field) }) : t(error_text)
      if (fieldValue === '') return errorText
      return isNotNullish(fieldValue) || errorText
    },
    email: (fieldValue: string) => {
      const pattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return pattern.test(fieldValue) || t(error_text)
    },
    min: (fieldValue: any) => {
      return (
        fieldValue.length >= arg ||
        t(error_text, {
          field: t(field),
          count: arg
        })
      )
    },
    max: (fieldValue: any) => {
      return (
        fieldValue.length <= arg ||
        t(error_text, {
          field: t(field),
          count: arg
        })
      )
    },
    min_price: (fieldValue: number) => {
      return (
        fieldValue < arg ||
        t(error_text, {
          field: t(field),
          count: arg
        })
      )
    },
    max_price: (fieldValue: number) => {
      return (
        fieldValue > arg ||
        t(error_text, {
          field: t(field),
          count: arg
        })
      )
    },
    price_between: (fieldValue: number) => {
      return (
        (fieldValue >= arg[0] && fieldValue <= arg[1]) ||
        t(error_text, {
          start: arg[0],
          end: formatPrice(arg[1], true),
          field: t(field)
        })
      )
    },
    percent_between: (fieldValue: number) => {
      return (
        (fieldValue >= arg[0] && fieldValue <= arg[1]) ||
        t(error_text, {
          start: arg[0],
          end: arg[1],
          field: t(field)
        })
      )
    },
    between: (fieldValue: string) => {
      return (
        (fieldValue.length >= arg[0] && fieldValue.length <= arg[1]) ||
        t(error_text, {
          start: arg[0],
          end: arg[1],
          field: t(field)
        })
      )
    },
    too_many_whitespaces: (fieldValue: any) => {
      const pattern = /^(\s+)|(\s+)$/g
      return (
        !pattern.test(fieldValue) ||
        t(error_text, {
          field: t(field)
        })
      )
    },
    type_string: (fieldValue: string) => {
      const pattern = /^[A-Z]*$/
      return pattern.test(fieldValue) || t(error_text)
    },
    type_number: (fieldValue: string) => {
      const pattern = /^[0-9]*$/
      return pattern.test(fieldValue) || t(error_text)
    },
    date_is_valid: (fieldValue: string) => {
      return isValidDate(fieldValue) || t(error_text)
    },
    max_months_purchase_date: (fieldValue: string) => {
      const fieldDay = parseDateString(fieldValue)
      return fieldDay.isSameOrAfter(now().subtract(6, 'months'), 'day') || t(error_text, { months: arg })
    },
    date_not_in_future: (fieldValue: string) => {
      const fieldDay = parseDateString(fieldValue)
      return fieldDay.isSameOrBefore(now()) || t(error_text)
    },
    date_min_age: (fieldValue: string) => {
      const fieldDay = parseDateString(fieldValue)
      const minDay = now().subtract(arg ?? 0, 'years')
      return fieldDay.isSameOrBefore(minDay) || t(error_text, { age: arg })
    }
  }

  return ruleMap[type]
}
